/**
 * Fires a confetti burst of neon squares + ribbons from a given origin.
 * Dependency-free. Creates a fullscreen canvas, animates, then removes itself.
 */
export function fireConfetti({ originX, originY, count = 140 } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:9999";
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }

  const ox = originX ?? canvas.width / 2;
  const oy = originY ?? canvas.height / 2;
  const colors = ["#00F0FF", "#FF00FF", "#39FF14", "#FFFFFF", "#FFD700"];

  const particles = Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 4 + Math.random() * 12;
    return {
      x: ox,
      y: oy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 4,
      size: 4 + Math.random() * 7,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.45,
      life: 1,
      shape: Math.random() > 0.5 ? "rect" : "strip",
    };
  });

  let prev = performance.now();
  const tick = (now) => {
    const dt = Math.min(40, now - prev) / 16.666;
    prev = now;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = false;
    for (const p of particles) {
      p.vy += 0.28 * dt;
      p.vx *= Math.pow(0.995, dt);
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      p.life -= 0.0075 * dt;
      if (p.life <= 0 || p.y > canvas.height + 40) continue;
      alive = true;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
      ctx.fillStyle = p.color;
      if (p.shape === "rect") {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 6, p.size, p.size / 3);
      }
      ctx.restore();
    }
    if (alive) {
      requestAnimationFrame(tick);
    } else {
      canvas.remove();
    }
  };
  requestAnimationFrame(tick);
}
